import { IssueFieldEnum } from "@mcapps/shared/enums";
import { IMapOfEvaluationCategoriesApi, IRisikoProbability } from "@mcapps/shared";
import { IIssueApi } from "@schemas/issueSchema";
import { ColDef, ColDefField, ICellRendererParams, ValueFormatterParams } from "ag-grid-community";
import React from "react";
import { getMatrixItemAndScore } from "@mcapps/shared/sharedUtils";
import { ISSUE_SECTIONS } from "../../../issueConstants";
import { IssueTableColumnContext } from "./issueTableColumnContext";

const getTwoLineHeaderWidth = (headerName: string) =>
  Math.max(100, Math.min(240, Math.ceil((headerName.length / 2) * 8 + 32)));

const hasRisikoMatrixInputs = (issue: IIssueApi | undefined, categoryKey: string, afterTiltak: boolean) => {
  const risiko = issue?.risiko;
  return afterTiltak
    ? risiko?.probabilityAfterTiltak != null && risiko.consequenceAfterTiltak?.[categoryKey] != null
    : risiko?.probability != null && risiko.consequence?.[categoryKey] != null;
};

export const getRisikoEvaluationColumns = ({
  columnContext,
}: {
  columnContext: IssueTableColumnContext;
}): ColDef<any>[] => {
  const { getIssueColumnBaseDef, hideAndSort, issueBoard } = columnContext;
  const categoriesFromBoard: IMapOfEvaluationCategoriesApi =
    issueBoard?.risikoSection?.evaluationCategoriesConfig ?? {};
  const evaluationCategories = Object.entries(categoriesFromBoard)
    .filter(([, value]) => value.isActive)
    .map(([key, value]) => ({
      key,
      ...value,
    }));
  const hasAfterTiltak = issueBoard?.risikoSection?.enableProbabilityAfterTiltak === true;
  const probabilityHeaderName = hasAfterTiltak ? "Sannsynlighet før tiltak" : "Sannsynlighet";
  const probabilityColumn: ColDef<any> = {
    ...getIssueColumnBaseDef(IssueFieldEnum.RISK_PROBABILITY),
    headerName: probabilityHeaderName,
    headerClass: "cell-px2 cell-py0 two-line-header",
    wrapHeaderText: true,
    initialWidth: getTwoLineHeaderWidth(probabilityHeaderName),
    editable: true,
    context: ISSUE_SECTIONS.RISIKO,
    valueFormatter: (params) => {
      const risikoItem = issueBoard?.risikoSection?.probabilites[params.value];
      return risikoItem?.name || "";
    },
    filter: "agFilter",
    cellEditor: "agRichSelectCellEditor",
    cellEditorParams: {
      values: Object.keys(issueBoard?.risikoSection?.probabilites || {}),
      valueFormatter: (params) => {
        const risikoItem = issueBoard?.risikoSection?.probabilites?.[params.value];
        return risikoItem?.name || "";
      },
    },
  };

  const probabilityAfterTiltakColumn: ColDef<any> = {
    field: "risiko.probabilityAfterTiltak",
    headerName: "Sannsynlighet etter tiltak",
    headerClass: "cell-px2 cell-py0 two-line-header",
    wrapHeaderText: true,
    initialWidth: getTwoLineHeaderWidth("Sannsynlighet etter tiltak"),
    editable: true,
    context: ISSUE_SECTIONS.RISIKO,
    valueFormatter: probabilityColumn.valueFormatter,
    filter: "agFilter",
    cellEditor: "agRichSelectCellEditor",
    cellEditorParams: probabilityColumn.cellEditorParams,
    ...hideAndSort("risiko.probabilityAfterTiltak"),
  };

  const buildEvaluationColumns = (afterTiltak = false): ColDef<any>[] =>
    evaluationCategories.flatMap((category) => {
      const consequenceField = afterTiltak
        ? `risiko.consequenceAfterTiltak.${category.key}`
        : `risiko.consequence.${category.key}`;
      const matrixValueField = afterTiltak
        ? `risikoMatrixValueAfterTiltak.${category.key}`
        : `risikoMatrixValue.${category.key}`;
      const suffix = afterTiltak ? " etter tiltak" : hasAfterTiltak ? " før tiltak" : "";
      const consequenceHeaderName = `${category?.name} konsekvens${suffix}`;
      const scoreHeaderName = `${category.name} poeng${suffix}`;
      const consequenceColDef: ColDef<any> = {
        field: consequenceField,
        headerName: consequenceHeaderName,
        headerClass: "cell-px2 cell-py0 two-line-header",
        wrapHeaderText: true,
        initialWidth: getTwoLineHeaderWidth(consequenceHeaderName),
        filter: "agFilter",
        editable: true,
        cellEditor: "agRichSelectCellEditor",
        valueFormatter: (params: ValueFormatterParams<IIssueApi>) => {
          const risikoItem: IRisikoProbability = categoriesFromBoard?.[category.key]?.consequences[params.value];
          return risikoItem?.name || "";
        },
        cellEditorParams: {
          values:
            Object.entries(categoriesFromBoard[category.key]?.consequences ?? {})
              .filter(([, obj]) => obj.isActive)
              .map(([id]) => id) ?? [],
          valueFormatter: (params) => {
            const risikoItem = categoriesFromBoard?.[category.key]?.consequences?.[params.value];
            return risikoItem?.name || "";
          },
        },
        context: ISSUE_SECTIONS.RISIKO,
        ...hideAndSort(consequenceField),
      };

      const matrixValueColDef: ColDef<IIssueApi> = {
        field: matrixValueField as ColDefField<IIssueApi>,
        headerName: scoreHeaderName,
        headerClass: "cell-px2 cell-py0 two-line-header",
        wrapHeaderText: true,
        initialWidth: getTwoLineHeaderWidth(scoreHeaderName),
        filter: "agNumberColumnFilter",
        valueGetter: (params) => {
          try {
            if (afterTiltak && !hasRisikoMatrixInputs(params.data, category.key, true)) return null;
            const matrixItemAndScore = getMatrixItemAndScore({
              issue: params.data,
              issueBoard,
              evaluationCategoryCapital: category.key,
              afterTiltak,
            });
            return matrixItemAndScore?.score;
          } catch (error) {
            return afterTiltak ? null : 0;
          }
        },
        cellRenderer: (params: ICellRendererParams<IIssueApi>) => {
          try {
            if (afterTiltak && !hasRisikoMatrixInputs(params.data, category.key, true)) return null;
            const matrixItemAndScore = getMatrixItemAndScore({
              issue: params.data,
              issueBoard,
              evaluationCategoryCapital: category.key,
              afterTiltak,
            });

            return (
              <div
                className="flex-items-center"
                style={{
                  display: "flex",
                  color: matrixItemAndScore?.matrixItem?.color != null ? "white" : "black",
                  backgroundColor: matrixItemAndScore?.matrixItem?.color,
                  fontSize: "12px",
                  borderRadius: "5px",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                {matrixItemAndScore?.score}
              </div>
            );
          } catch (error) {
            return afterTiltak ? null : <div>-</div>;
          }
        },
        context: ISSUE_SECTIONS.RISIKO,
        ...hideAndSort(matrixValueField),
      };

      return [consequenceColDef, matrixValueColDef];
    });

  const evaluationColumns = buildEvaluationColumns();
  const evaluationColumnsAfterTiltak = hasAfterTiltak ? buildEvaluationColumns(true) : [];

  const categoryCommentCol: ColDef<IIssueApi> = {
    field: `risiko.categoryComment`,
    headerName: "Risiko kommentar",
    headerClass: "cell-px2 cell-py0 two-line-header",
    wrapHeaderText: true,
    initialWidth: getTwoLineHeaderWidth("Risiko kommentar"),
    minWidth: 120,
    filter: "agTextColumnFilter",
    editable: true,
    context: ISSUE_SECTIONS.RISIKO,
    ...hideAndSort(`risiko.categoryComment`),
  };
  const buildSummaryColumn = (afterTiltak = false, worstScore = false): ColDef<any> => {
    const matrixValuePrefix = afterTiltak ? "risikoMatrixValueAfterTiltak" : "risikoMatrixValue";
    const field = afterTiltak
      ? worstScore
        ? "risiko.worstScoreAfterTiltak"
        : "risiko.pointTotalAfterTiltak"
      : worstScore
      ? "risiko.worstScore"
      : "risiko.pointTotal";
    const suffix = afterTiltak ? " etter tiltak" : hasAfterTiltak ? " før tiltak" : "";
    const headerName = `${worstScore ? "Verste kategori" : "Total poengsum"}${suffix}`;

    return {
      field,
      headerName,
      headerClass: "cell-px2 cell-py0 two-line-header",
      wrapHeaderText: true,
      initialWidth: getTwoLineHeaderWidth(headerName),
      filter: "agNumberColumnFilter",
      context: ISSUE_SECTIONS.RISIKO,
      ...hideAndSort(field),
      valueGetter: (params) => {
        try {
          const values = evaluationCategories.map((category) =>
            params.getValue(`${matrixValuePrefix}.${category.key}`),
          );
          if (afterTiltak && values.some((value) => value == null)) return null;
          const numericValues = values.map((value) => Number(value) || 0);
          return worstScore ? Math.max(0, ...numericValues) : numericValues.reduce((sum, value) => sum + value, 0);
        } catch (error) {
          return afterTiltak ? null : 0;
        }
      },
    };
  };

  const pointTotalCol = buildSummaryColumn();
  const lowestPointCol = buildSummaryColumn(false, true);
  const afterTiltakSummaryColumns =
    issueBoard?.risikoSection?.enableProbabilityAfterTiltak === true
      ? [buildSummaryColumn(true), buildSummaryColumn(true, true)]
      : [];

  return [
    probabilityColumn,
    ...evaluationColumns,
    pointTotalCol,
    lowestPointCol,
    ...(hasAfterTiltak
      ? [probabilityAfterTiltakColumn, ...evaluationColumnsAfterTiltak, ...afterTiltakSummaryColumns]
      : []),
    categoryCommentCol,
  ];
};
